<template>
  <section class="full-width calculator-container">
    <div class="calculator-background picturefill-background is-lazy">
      <span class="picturefill-background-source" data-src="/Static/images/blurry-background-mobile-opt.png"></span>
      <span
        class="picturefill-background-source"
        data-src="/Static/images/blurry-background-tablet-opt.png"
        data-media="(min-width: 576px)"
      ></span>
      <span
        class="picturefill-background-source"
        data-src="/Static/images/blurry-background-opt.png"
        data-media="(min-width: 1024px)"
      ></span>
    </div>

    <div class="container-fluid">
      <div class="loan-calculator">
        <h2>{{ title }}</h2>
        <div v-if="hasSubtitle" v-html="subtitle" class="loan-calculator__subtitle"></div>
        <form action @submit="gtm">
          <label for="amount" class="loan-calculator__label">
            {{ questionAmountToBorrow }}
          </label>
          <div class="loan-calculator__input-container">
            <span>£</span>
            <input
              type="number"
              id="amount"
              v-model.number="loanAmountObj"
              class="loan-calculator__input"
              min="500"
              max="25000"
              pattern="[0-9]*"
              @keypress="onKeypress()"
              @input="calculateValues()"
              required
            />
            <div class="loan-calculator__input-validation">
              <small>Loan amount from £500 to £25,000</small>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20">
                <use xlink:href="/Static/images/sprites/symbols.svg#icon-info" />
              </svg>
            </div>
          </div>

          <label for="terms" class="loan-calculator__label">{{ questionTermOfRepayment }}</label>
          <select
            id="terms"
            class="calculator-select loan-calculator__select"
            v-model.number="loanTermobj"
            @change="calculateValues()"
          >
            <option value="12">1 year</option>
            <option value="24">2 years</option>
            <option value="36">3 years</option>
            <option value="48">4 years</option>
            <option value="60">5 years</option>
            <option value="72">6 years</option>
            <option value="84">7 years</option>
          </select>

          <label for="apr" class="loan-calculator__label">{{ questionAprInterestRate }}</label>
          <select
            id="apr"
            class="calculator-select loan-calculator__select"
            v-model.number="selectedLoanApr"
            @change="calculateValues()"
          >
            <option v-for="option in loanAprOptions" :key="option" :value="Number(parseFloat(option) / 100)">
              {{ option }}%
            </option>
          </select>

          <p class="loan-calculator__subtitle">{{ subHeadingPaymentIllustration }}</p>

          <ul class="loan-calculator__results">
            <li>
              {{ breakdownMonthlyRepayments }}
              <strong>{{ monthlyRepaymentobj }}</strong>
            </li>
            <li>
              {{ breakdownTotalRepayments }}
              <strong>{{ totalRepayableAmountobj }}</strong>
            </li>
          </ul>

          <a @click.prevent="submitCalculator" :href="buttonUrl" class="btn btn--blue">{{ getAQuoteButtonText }}</a>
        </form>
        <p class="loan-calculator__cta-subtitle" v-if="hasGetAQuoteSubtext">{{ getAQuoteSubtext }}</p>
      </div>
    </div>
  </section>
</template>

<script>
import lazyloadPicturefillBackground from 'lazyload-picturefill-background';

export default {
  name: 'LoanCalculator',
  props: {
    title: {
      type: String,
      default: 'Tell us about your loan',
    },
    subtitle: {
      type: String,
    },
    questionAmountToBorrow: {
      type: String,
      default: '1. How much would you like to borrow?',
    },
    questionTermOfRepayment: {
      type: String,
      default: '2. How quickly would you like to repay it?',
    },
    questionAprInterestRate: {
      type: String,
      default: '3. APR interest rate',
    },
    aprRangeMin: {
      type: String,
      default: '0.1',
    },
    aprRangeMax: {
      type: String,
      default: '20.9',
    },
    aprRangeDefault: {
      type: String,
      default: '0.1',
    },
    subHeadingPaymentIllustration: {
      type: String,
      default: 'Your repayment illustration',
    },
    breakdownTotalRepayments: {
      type: String,
      default: 'Total repayments:',
    },
    breakdownMonthlyRepayments: {
      type: String,
      default: 'Monthly repayments',
    },
    getAQuoteButtonLink: {
      type: String,
    },
    getAQuoteButtonLinkTarget: {
      type: String,
    },
    getAQuoteButtonText: {
      type: String,
      default: 'Get a quote',
    },
    getAQuoteSubtext: {
      type: String,
    },
  },
  data: function () {
    return {
      selectedLoanApr: Number(this.aprRangeDefault / 100),
      loanAmountObj: 5000,
      loanTermobj: 36,
      loanAprOptions: [],
      totalRepayableAmountobj: null,
      monthlyRepaymentobj: null,
    };
  },
  computed: {
    buttonUrl() {
      // example url https://loans.asda.com/?advsrcSuggest=ASDW01&source=ASDW01&loanAmount=5000&loanTerm=48&utm_campaign=My-Campaign&gclid=123456abcd
      let url = new URL(this.getAQuoteButtonLink, window.location.href);
      url.searchParams.set('loanAmount', this.loanAmountObj);
      url.searchParams.set('loanTerm', this.loanTermobj);
      return url.href;
    },
    hasSubtitle() {
      return !!this.subtitle;
    },
    hasGetAQuoteSubtext() {
      return !!this.getAQuoteSubtext;
    },
  },

  methods: {
    onKeypress(e) {
      e = e ? e : window.event;
      let key = e.keyCode || e.which;
      key = String.fromCharCode(key);
      const regex = /[0-9]/;
      if (!regex.test(key)) {
        e.returnValue = false;
        if (e.preventDefault) {
          e.preventDefault();
        }
      }
    },

    submitCalculator() {
      // track our gtm data
      this.gtm();
      window.open(this.buttonUrl);
    },

    calculateValues() {
      if (this.loanAmountObj > 0) {
        let monthlyRateActual = Math.pow(this.selectedLoanApr + 1, 1 / 12) - 1;
        let monthlyRepaymentActual =
          (this.loanAmountObj * monthlyRateActual * Math.pow(1 + monthlyRateActual, this.loanTermobj)) /
          (Math.pow(1 + monthlyRateActual, this.loanTermobj) - 1);
        let monthlyRepaymentRounded = Math.floor(monthlyRepaymentActual * 100) / 100;
        this.monthlyRepaymentobj = '£' + this.formatNumber(monthlyRepaymentRounded, 2);

        let totalRepayableAmount = monthlyRepaymentRounded * this.loanTermobj;
        this.totalRepayableAmountobj = '£' + this.formatNumber(totalRepayableAmount, 2);
      } else {
        this.monthlyRepaymentobj = '£0';
        this.totalRepayableAmountobj = '£0';
      }
    },

    generateAprOptions() {
      const minValue = parseFloat(this.aprRangeMin);
      const maxValue = parseFloat(this.aprRangeMax);
      const increment = 0.1;
      const numberOfOptions = Math.floor((maxValue - minValue) / increment) + 2;
      for (let i = 0; i < numberOfOptions; i++) {
        const value = (minValue + i * increment).toFixed(1);
        this.loanAprOptions.push(value);
      }
      if (!this.loanAprOptions.includes(this.aprRangeDefault)) this.selectedLoanApr = this.loanAprOptions[0];
    },

    // function used to format total and monthly repayments
    // bascially rounds whatever is passed in to two decimal places
    formatNumber(e, decimalPlaces) {
      if (e != '') {
        let valueArr = parseFloat(e.toString().replace(/,/g, '')).toFixed(decimalPlaces).split('.');
        valueArr[0] = parseFloat(valueArr[0]).toLocaleString('en-GB');
        return valueArr.join('.');
      }
    },

    gtm() {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'personalLoansCalculator',
        borrowAmount: this.loanAmountObj,
        repayPeriod: this.loanTermobj / 12,
        totalRepayments: this.totalRepayableAmountobj.replace('£', ''),
        monthlyRepayment: this.monthlyRepaymentobj.replace('£', ''),
        illustrativeAPR: this.selectedLoanApr,
        outputValue: null,
      });
    },
  },

  created() {
    this.calculateValues();
  },

  mounted() {
    this.generateAprOptions();
    new lazyloadPicturefillBackground();
  },
};
</script>
